import React from 'react'
import moment from 'moment'
import { withStyles, createStyles } from '@mui/styles'
import { Paper, Tooltip, Typography, IconButton } from '@mui/material'
import { Room, History } from '@mui/icons-material'
import { muiOptions, defaultStyles } from '../../../infrastructure/materialUiThemeProvider'
import { t } from '../../../infrastructure/i18nextHelper'
import { Popup, Stepper, AttachFileButton, Button, ButtonPopupParams, AttachedDocument } from '../../common/customComponents'
import { activateVesselReferenceIdMasterDataFeature, getVesselFinderId, ProductQuantity, VesselProduct, VesselStatus } from '../vesselModels'
import * as api from '../../../infrastructure/api'
import { FirstLine, SecondLine, ThirdLine, FourthLine, FifthLine } from './_vesselSection'
import ProductsSection from './_vesselProductSection'
import { snackbars } from '../../../infrastructure/snackbars'
import { Claims } from '../../../infrastructure/signIn/models'
import { hasClaim } from '../../../infrastructure/signIn/userContext'
import { hasFeature } from '../../../infrastructure/feature'
import { useActionDebounce } from '../../common/debounce'
import { VesselEditContainer, VesselEditDialogContainer, vesselDialog } from './vesselEditStore'
import { VesselStatusChip } from '../statusChip'
import { historyDialog } from '../../common/history/dialog'
import { ActionDescriptor } from 'src/app/common/components/table/table'
import { VesselAnalyzeDocumentButton, DocumentAnalyzed, DocumentType, Product, VesselFormExtract } from './vesselAnalyseDocumentButton'

function VesselEdit({ classes }) {
    let vessel = VesselEditContainer.useContainer()
    let vesselDialogStore = VesselEditDialogContainer.useContainer()
    let handleSaveVesselDebouncer = useActionDebounce(save)
    let trackVesselId = vessel.state?.referenceID ?? ''
    if (trackVesselId && vessel.state && hasFeature(activateVesselReferenceIdMasterDataFeature))
        trackVesselId = getVesselFinderId(trackVesselId)

    async function save() {
        vessel.formErrors.highlightUpdateErrors()
        if (vessel.formErrors.vesselFieldStatus.getFieldsWithStatus('alert').length === 0) {
            await vessel.trySave()
            return
        }
        snackbars.warning(vessel.formErrors.isReferenceIdMissing()
            ? t('warnings.referenceIdMandatory')
            : t('warnings.mandatoryFieldsBeforeSaving'))
    }

    let handleChangeStatus = (newStatus: VesselStatus) => {
        if (!vessel.state) return
        if (newStatus === VesselStatus.Actual) {
            vessel.formErrors.highlightSettingActualStatusErrors()
            if (vessel.formErrors.vesselProductFieldStatus.getStatus('loadPort') === 'alert' ||
                vessel.formErrors.vesselFieldStatus.getStatus('counterpartySupplierId') === 'alert') {
                snackbars.warning(t('warnings.mandatoryFieldsBeforeStatusChange'))
                return
            }
        }

        vessel.setState({ ...vessel.state, status: newStatus })
        vessel.formErrors.vesselProductFieldStatus.clearStatuses()
        vessel.formErrors.vesselFieldStatus.clearStatuses()
    }

    let handleDocumentAnalyzed = (document: DocumentAnalyzed) => {
        switch (document.type) {
            case DocumentType.VESSEL:
                handleVesselProductAnalyzed(document.data)
                break
            case DocumentType.PURCHAGE_MOVEMENTS:
                handleVesselPurchagedMovementAnalyzed(document.data)
                break
            default:
                break
        }
    }

    let handleVesselProductAnalyzed = async (data: VesselFormExtract) => {
        if (!vessel.state) return

        let patchesById: { [id: string]: VesselProduct } = {}
        let newProducts: VesselProduct[] = []
        let unmatchedTypes: string[] = []

        let buildPatchedProduct = (product: VesselProduct, entry: Product): VesselProduct => {
            let patchQuantities = (current: ProductQuantity, operationType: string): ProductQuantity => {
                let quantities = entry.productOperations.find(op => op.operationType === operationType)?.quantities
                return {
                    ...current,
                    // FIXME: Currently only handling 'TonsAir', 'BBL', and 'M15' units. Consider making this more flexible.
                    quantity: quantities?.find(x => x.unit === 'TonsAir')?.value ?? current.quantity,
                    quantityOverwritten: true,
                    barrel: quantities?.find(x => x.unit === 'BBL')?.value ?? current.barrel,
                    barrelOverwritten: true,
                    volume: quantities?.find(x => x.unit === 'M15')?.value ?? current.volume,
                    volumeOverwritten: true
                }
            }

            return {
                ...product,
                billOfLading: patchQuantities(product.billOfLading, 'B/L'),
                outturn: patchQuantities(product.outturn, 'Outturn')
            }
        }

        for (let entry of data.products) {
            let existingProduct = vessel.state.products.find(x =>
                vessel.products[x.productId]?.toLowerCase().trim() === entry.productType.toLowerCase().trim()
            )

            if (existingProduct) {
                patchesById[existingProduct.id] = buildPatchedProduct(existingProduct, entry)
            } else {
                let candidateId = Object.keys(vessel.products).find(id =>
                    vessel.products[id].toLowerCase().trim() === entry.productType.toLowerCase().trim() &&
                    vessel.state.products.findIndex(p => p.productId === id) < 0
                )

                if (candidateId && vessel.state.jettyCode) {
                    try {
                        let fetched = await api.get<VesselProduct>(`vessel/${vessel.state.id}/product/${candidateId}?jetty=${vessel.state.jettyCode}`)
                        let preparedProduct = { ...fetched, canRemove: true, isActive: true }
                        let patchedProduct = buildPatchedProduct(preparedProduct, entry)
                        newProducts.push(patchedProduct)
                    } catch (error) {
                        unmatchedTypes.push(entry.productType)
                    }
                } else {
                    unmatchedTypes.push(entry.productType)
                }
            }
        }

        if (newProducts.length > 0) {
            vessel.updateVesselProducts(newProducts)
            vessel.updateCostingInformations(newProducts)
        }

        vessel.setState({
            ...vessel.state,
            name: data.vesselName,
            // Preserve the former backend UtcDateTime conversion for the form's DateTime value.
            completionOfDischargeDate: moment.utc(data.outturnDate).format('YYYY-MM-DDTHH:mm:ss'),
            completionOfDischargeDateOverwritten: true,
            products: vessel.state.products.map(x => patchesById[x.id] ?? x)
        })
        vessel.formErrors.vesselFieldStatus.setStatus(['name', 'completionOfDischargeDate'], 'info')
        vessel.formErrors.vesselProductFieldStatus.setStatus(['billOfLading', 'outturn'], 'info')

        if (unmatchedTypes.length > 0) {
            snackbars.warning(t('vessels.label.analyzeUnmatchedProducts', { products: unmatchedTypes.join(', ') }))
        }
    }

    let handleVesselPurchagedMovementAnalyzed = async (data: VesselFormExtract) => {
        if (!vessel.state) return

        let outturnProducts = data.products.filter(product =>
            product.productOperations.some(operation => operation.operationType === 'Outturn')
        )

        // FIXME: for the purpose of the POC the name of the company is hardcoded
        // since it's the only company involved in the POC
        const companyName = "TEMDG"
        let companyCode = Object.keys(vessel.companys).find(code =>
            vessel.companys[code].trim().toUpperCase() === companyName.trim().toUpperCase())
        if (!companyCode) return

        const otherCompanyName = "Others"
        let otherCompanyCode = Object.keys(vessel.companys).find(code =>
            vessel.companys[code].trim().toUpperCase() === otherCompanyName.trim().toUpperCase())

        let productCodeProductIds = vessel.getProductCodeProductIds()
        let productsIds = outturnProducts
            .map(product => productCodeProductIds[product.productType.toLocaleUpperCase().trim()])
            .filter(productId => productId !== undefined)
        await vessel.addProducts(productsIds)
        for (let productId of productsIds) {
            await vessel.addAllPurchaseMovements(productId)
        }

        vessel.setPurchaseMovementsByVesselProductId(previous => {
            let updated = { ...previous }
            for (let entry of outturnProducts) {
                // FIXME: remove the hardcoded company name 'Outturn', 'M15', 'TonsAir'
                // those values are returned by the LLM after the analysis of the document in MADAGASCAR
                // check : 
                let outturn = entry.productOperations.find(operation => operation.operationType === 'Outturn')
                let m15Volume = outturn?.quantities.find(quantity => quantity.unit === 'M15')?.value
                let tonsAirVolume = outturn?.quantities.find(quantity => quantity.unit === 'TonsAir')?.value
                let productId = productCodeProductIds[entry.productType.toLocaleUpperCase().trim()]
                let product = vessel.state.products.find(x => x.productId === productId)
                if (!product || !previous[product.id]) continue

                updated[product.id] = previous[product.id].map(movement => {
                    if (movement.companyCode !== companyCode || !movement.sites.length)
                        return movement
                    let [firstSite, ...otherSites] = movement.sites
                    return {
                        ...movement,
                        sites: [{
                            ...firstSite,
                            volume: m15Volume ?? movement.sites[0].volume,
                            volumeOverride: true,
                            quantity: tonsAirVolume ?? movement.sites[0].quantity,
                            quantityOverride: true
                        }, ...otherSites]
                    }
                })
                if (!otherCompanyCode) {
                    continue
                }
                updated[product.id] = updated[product.id].map(movement => {
                    if (movement.companyCode !== otherCompanyCode || !movement.sites.length)
                        return movement
                    let [firstSite, ...otherSites] = movement.sites
                    let volumeComputed = (product.outturn.volume != null && m15Volume != null)
                        ? product.outturn.volume - m15Volume
                        : movement.sites[0].volume  //old value
                    let quantityComputed = (product.outturn.quantity != null && tonsAirVolume != null)
                        ? product.outturn.quantity - tonsAirVolume
                        : movement.sites[0].quantity  //old value
                    return {
                        ...movement,
                        sites: [{
                            ...firstSite,
                            volume: volumeComputed,
                            volumeOverride: true,
                            quantity: quantityComputed,
                            quantityOverride: true
                        }, ...otherSites]
                    }
                })
            }
            return updated
        })
    }

    let buttons: ButtonPopupParams[] = [{
        buttonEffect: vessel.canEdit ? handleSaveVesselDebouncer.execute : undefined,
        buttonText: vessel.canEdit ? t('components.updateButton') : '',
    }]

    return (
        <Popup title={t('vessels.popup.title')}
            close={vesselDialog.close}
            isOpen={vesselDialogStore.isOpen}
            buttons={buttons}>
            {!vessel.state ? <div />
                : <div>
                    <Paper className={classes.stepperAndDocsDiv}>
                        <div className={classes.stepper}>
                            <Stepper steps={['', '', '']}
                                icons={
                                    [VesselStatus.Forecast, VesselStatus.Planned, VesselStatus.Actual].map(x =>
                                        <VesselStatusChip key={x} status={x} disabled={vessel.state.status !== x} hoverable />)}
                                activeStep={vessel.state.status as number}
                                ignoreStepStyles
                                classesOverride={{ root: classes.vesselStepper }}
                                onStepChange={(index: number) => handleChangeStatus(index as VesselStatus)} />
                        </div>
                        {!!trackVesselId &&
                            <Tooltip title={<Typography variant='body1'>{t('vessels.label.localizeVessel')}</Typography>}
                                placement='bottom'>
                                <span>
                                    <Button
                                        label={t('vessels.label.trackVessel')}
                                        img={<Room />}
                                        className={classes.trackVesselButton}
                                        onClick={() => {
                                            window.open(`https://www.vesselfinder.com/?imo=${trackVesselId}`)
                                        }} />
                                </span>
                            </Tooltip>}
                        {hasClaim(Claims.VesselReader) || hasClaim(Claims.VesselWriter) || hasClaim(Claims.VesselManager)
                            ? <AttachFileButton
                                id='upload-doc-button'
                                className={classes.documentButton}
                                title={t('vessels.label.documentDialogTitle')}
                                disableDelOrAdd={!hasClaim(Claims.VesselWriter) && !hasClaim(Claims.VesselManager)}
                                context={vessel.jettys.find(x => x.code === vessel.state.jettyCode)?.country ?? ''}
                                keyTemplate='vessel-{vesselId}'
                                keyParameters={{ vesselId: vessel.state?.id ? vessel.state.id.replace(/-/g, '') : '' }}
                                additionalDocumentActions={hasFeature("AiDocumentAnalysis")
                                    ? [{
                                        name: 'analyzeDocument',
                                        isBodyAction: true,
                                        noMarginOnIcon: true,
                                        bodyContentOverride: (document: AttachedDocument) => (
                                            <VesselAnalyzeDocumentButton
                                                enabled={vessel.state.jettyCode !== null && vessel.state.jettyCode !== ""}
                                                document={document}
                                                onAnalyzed={handleDocumentAnalyzed}
                                            />
                                        )
                                    }]
                                    : []
                                }
                            />
                            : null
                        }
                    </Paper>

                    <Paper className={classes.vesselInformations}>
                        <div className={classes.headerRight}>
                            <Tooltip title={<Typography variant='body1'>{t('history.open')}</Typography>} placement='top'>
                                <IconButton onClick={() => historyDialog.open({ id: vessel.state?.id, type: 'vessel', name: `${vessel.state?.name ?? ''}` })}>
                                    <History />
                                </IconButton>
                            </Tooltip>
                        </div>
                        <FirstLine />
                        <SecondLine />
                        <ThirdLine />
                        <FourthLine />
                        <FifthLine />
                    </Paper>
                    <Paper className={classes.productInformation}>
                        <ProductsSection productCodeByProductId={vessel.products} />
                    </Paper>
                </div>}
        </Popup>
    )
}

let styles = () =>
    createStyles({
        vesselInformations: {
            padding: '0.5em',
            marginTop: '1em',
            marginBottom: '0.5em'
        },
        vesselStepper: {
            backgroundColor: 'white'
        },
        stepperAndDocsDiv: {
            ...defaultStyles.flexRow,
            marginTop: '1em',
            width: '100%'
        },
        stepper: { width: '97%' },
        productInformation: {
            padding: '0.5em',
            marginTop: '1em',
            marginBottom: '0.5em'
        },
        documentButton: {
            marginRight: '0.5em'
        },
        trackVesselButton: {
            ...defaultStyles.secondaryButton,
            marginRight: '0.5em'
        },
        headerRight: {
            ...defaultStyles.flexRow,
            justifyContent: 'flex-end',
            marginBottom: '0.5em',
            marginTop: '-1em'
        },
    })

export default withStyles(styles, muiOptions)(VesselEdit)